import { Link, useLocation } from "react-router-dom";

import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import DefaultFooter from "examples/Footers/DefaultFooter";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import ColoredBackgroundCard from "examples/Cards/BackgroundCards/ColoredBackgroundCard";
import InfoBackgroundCard from "examples/Cards/BackgroundCards/InfoBackgroundCard";
import SimpleBlogCard from "examples/Cards/BlogCards/SimpleBlogCard";

import footerRoutes from "footer.routes";
import routes from "routes";
import SectionHeading from "studio-edifique/components/SectionHeading";
import SeoHead from "studio-edifique/components/SeoHead";
import {
  blogPosts,
  company,
  createBreadcrumbSchema,
  createOrganizationSchema,
  featuredBlogPost,
  heroImages,
} from "studio-edifique/siteContent";

function BlogPage() {
  const { pathname } = useLocation();
  const spotlightPosts = blogPosts.slice(1, 5);
  const remainingPosts = blogPosts.slice(5);

  return (
    <>
      <SeoHead
        title="Blog Técnico"
        description="Conteúdos técnicos da Studio Edifique sobre obra, projeto arquitetônico, estrutura, sondagem, slump test e administração de obra."
        keywords={[
          ...company.seoKeywords,
          "blog de arquitetura",
          "blog de engenharia",
          "conteúdo técnico de obra",
        ]}
        path={pathname}
        image={heroImages.blog}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Início", path: "/" },
            { name: "Blog", path: pathname },
          ]),
        ]}
      />
      <DefaultNavbar
        brand={company.brandName}
        routes={routes}
        action={{
          type: "external",
          route: company.whatsappUrl,
          label: "Conversar agora",
          color: "info",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="52vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba } }) =>
            `${linearGradient(rgba("#201E1D", 0.9), rgba("#111111", 0.52))}, url(${
              heroImages.blog
            })`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography variant="button" textTransform="uppercase" letterSpacing={2} color="info">
              Conteúdo técnico
            </MKTypography>
            <MKTypography variant="h2" component="h1" color="white" mt={1}>
              Conteúdo profissional para quem quer construir, reformar ou decidir com mais base
              técnica.
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.84} mt={2}>
              Artigos produzidos para melhorar entendimento de projeto, obra, estrutura e controle
              técnico.
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <Grid
              container
              item
              xs={12}
              lg={7}
              flexDirection="column"
              justifyContent="center"
              mx="auto"
              mb={6}
            >
              <MKBox py={2} px={{ xs: 0, md: 6 }} textAlign="center">
                <MKBox
                  width="4rem"
                  height="4rem"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  variant="gradient"
                  bgColor="info"
                  color="white"
                  shadow="md"
                  borderRadius="lg"
                  mx="auto"
                >
                  <Icon fontSize="medium">article</Icon>
                </MKBox>
                <MKTypography variant="h2" mt={2} mb={1}>
                  Conteúdos para orientar melhores decisões
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  Temas práticos de arquitetura, engenharia, estrutura e obra para entender etapas,
                  comparar decisões e planejar os próximos passos.
                </MKTypography>
              </MKBox>
            </Grid>

            <Grid container spacing={3} mb={6}>
              <Grid item xs={12} md={6} lg={4}>
                <ColoredBackgroundCard
                  image={featuredBlogPost.heroImage}
                  label={featuredBlogPost.category}
                  title={featuredBlogPost.title}
                  description={featuredBlogPost.excerpt}
                  action={{
                    type: "internal",
                    route: `/blog/${featuredBlogPost.slug}`,
                    label: "Ler artigo",
                  }}
                />
              </Grid>
              <Grid container spacing={3} item xs={12} md={6} lg={8}>
                {spotlightPosts.map((post) => (
                  <Grid item xs={12} md={6} key={post.slug}>
                    <MKBox
                      component={Link}
                      to={`/blog/${post.slug}`}
                      sx={{ display: "block", textDecoration: "none", height: "100%" }}
                    >
                      <InfoBackgroundCard
                        image={post.heroImage}
                        icon={post.category === "Estrutura" ? "foundation" : "menu_book"}
                        title={post.title}
                        label={post.readingTime}
                      />
                    </MKBox>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <SectionHeading
              eyebrow="Mais artigos"
              title="Leitura complementar para aprofundar o planejamento da obra"
              description="Outros conteúdos do blog que reforçam economia inteligente, clareza técnica e prevenção de falhas."
            />
            <Grid container spacing={4}>
              {remainingPosts.map((post) => (
                <Grid item xs={12} md={6} key={post.slug}>
                  <SimpleBlogCard
                    image={post.coverImage}
                    title={post.title}
                    description={post.excerpt}
                    action={{
                      type: "internal",
                      route: `/blog/${post.slug}`,
                      color: "dark",
                      label: "Ler post",
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            <MKBox
              mt={8}
              p={4}
              borderRadius="xl"
              sx={{
                background: ({ palette: { grey } }) => grey[100],
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} lg={8}>
                  <SectionHeading
                    eyebrow="Quer apoio técnico"
                    title="Conteúdo ajuda. A decisão certa em obra pede acompanhamento."
                    description="Se você precisa transformar informação em ação prática, fale com a Studio Edifique."
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <MKBox display="flex" flexDirection="column" gap={1.5}>
                    <MKButton
                      component="a"
                      href={company.whatsappUrl}
                      target="_blank"
                      rel="noreferrer"
                      variant="gradient"
                      color="info"
                    >
                      Falar no WhatsApp
                    </MKButton>
                    <MKButton component={Link} to="/portfolio" variant="outlined" color="dark">
                      Ver portfólio
                    </MKButton>
                  </MKBox>
                </Grid>
              </Grid>
            </MKBox>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default BlogPage;
